import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  ApiError,
  Button,
  CASE_STATUSES,
  IncompleteProfileMarker,
  Spinner,
  cn,
  type CaseStatus,
  type PartnerEnrollmentPage,
  type PartnerEnrollmentRow,
} from "@dearbill/shared";
import { useApi } from "../auth/useApi";
import { usePartnerMe } from "../auth/usePartnerMe";
import { useOrg } from "../shell/OrgContext";
import { ReferSomeoneModal } from "../shell/ReferSomeoneModal";
import { CASE_STATUS_CHIP_CLASS } from "../components/referrals/caseStatus";
import { CaseStatusLabel } from "../components/referrals/CaseStatusLabel";
import iconPerson from "../assets/icon-person.svg";

/** How many loaded rows each "Show more" click reveals. */
const PAGE = 8;

/** Parse `?status=` into a case status; null when absent or not in the vocabulary. */
function caseStatusFromParam(value: string | null): CaseStatus | null {
  return CASE_STATUSES.find((cs) => cs === value) ?? null;
}

/** Month and day ("Sep 15") for the Referred column. */
function fmtSent(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * The Last change column's value: relative within the past week ("Today",
 * "Yesterday", "2 days ago"), month and day beyond it (Figma dR8cJ6hRSW5eERToo64boe
 * node 2340:385 mixes both). A future or unparseable stamp falls back to the
 * short date.
 */
function fmtLastChange(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const startOfDay = (x: Date): number =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days > 1 && days < 7) return `${days.toString()} days ago`;
  return fmtSent(iso);
}

/**
 * Referrals tab (`/orgs/:orgId/forms`).
 *
 * Visible to every member, since a regular `user` needs it to reach Refer a
 * patient. The list endpoint is role-scoped server-side: a `user` gets only
 * their own referrals and an `org_admin` gets the whole org's. The tab renders
 * whatever comes back, with no client-side role filtering; the subtitle marks
 * the org-wide list as "Manager view" for an `org_admin`.
 *
 * Refer a patient opens the shared `ReferSomeoneModal`. The Status column
 * shows the case status read off the wire (`caseStatus`, derived
 * server-side). Program names appear only in the Current status column,
 * which shows `caseStatusDetail`.
 *
 * The list is server-paginated (`PartnerEnrollmentPage`: newest first, opaque
 * `nextCursor`). The footer button first reveals `PAGE` more of the loaded
 * rows. Once every loaded row shows and the server has more, it becomes Load
 * more and appends the next server page. The chip counts and the Find filter
 * cover the loaded rows only.
 *
 * An unshared broker referral arrives as the name-and-date-only
 * `PartnerEnrollmentRedactedSummary` (`resultsShared: false`). Its status
 * columns read Not shared, it never matches a status chip, and it has no Open
 * link because the detail route 404s it.
 */
export function EnrollmentsTab(): ReactNode {
  const api = useApi();
  const navigate = useNavigate();
  const { orgId, res, isOrgAdmin } = useOrg();
  const { me } = usePartnerMe();

  const [rows, setRows] = useState<PartnerEnrollmentRow[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [referOpen, setReferOpen] = useState(false);

  // The filter initializes from `?status=`, which the Home tiles link with; an
  // absent or unknown value means All. Chip changes write it back so the URL
  // stays shareable.
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<CaseStatus | null>(() =>
    caseStatusFromParam(searchParams.get("status")),
  );
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(PAGE);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setNextCursor(null);
    setLoadError(null);
    api<PartnerEnrollmentPage>(`/api/partner/orgs/${orgId}/enrollments`)
      .then((page) => {
        if (cancelled) return;
        setRows(page.items);
        setNextCursor(page.nextCursor);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // A 401 has cleared the session in `useApi`, and the redirect takes over.
        if (err instanceof ApiError && err.status === 401) return;
        setLoadError("We couldn't load referrals. Please try again.");
      });
    return () => {
      cancelled = true;
    };
  }, [api, orgId]);

  const onLoadMore = async (): Promise<void> => {
    if (nextCursor === null) return;
    setLoadingMore(true);
    setLoadError(null);
    try {
      const page = await api<PartnerEnrollmentPage>(
        `/api/partner/orgs/${orgId}/enrollments?cursor=${encodeURIComponent(nextCursor)}`,
      );
      setRows((prev) => [...(prev ?? []), ...page.items]);
      setNextCursor(page.nextCursor);
      // Reveal the next slice of the newly loaded rows at once, since the click
      // asked to see more rows.
      setVisible((v) => v + PAGE);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 401) return;
      setLoadError("We couldn't load referrals. Please try again.");
    } finally {
      setLoadingMore(false);
    }
  };

  // Mirrors the server's `can_take_referrals()` gate: a Provider org mints
  // referral links on membership alone, and other org types must be active
  // (the endpoints answer 403 or 409 otherwise).
  const canEnroll = res.profile.organizationType === "Provider" || res.status === "active";

  const byLabel = (row: PartnerEnrollmentRow): string =>
    me && row.partnerUserId === me.userId ? "You" : row.partnerUserName || "—";

  // An unshared broker row has the redacted shape with no case status, so it
  // counts toward All only.
  const counts = new Map<CaseStatus, number>();
  for (const r of rows ?? []) {
    if (r.resultsShared && r.caseStatus !== null)
      counts.set(r.caseStatus, (counts.get(r.caseStatus) ?? 0) + 1);
  }

  const filtered = (rows ?? []).filter((r) => {
    // A redacted row has no case status, so any status chip excludes it.
    if (filter !== null && (!r.resultsShared || r.caseStatus !== filter)) return false;
    if (query.trim() !== "") {
      const q = query.trim().toLowerCase();
      // A redacted row shares its name but withholds the phone entirely.
      const nameHit = r.fullName.toLowerCase().includes(q);
      const digits = q.replace(/\D/g, "");
      const phoneHit =
        r.resultsShared && digits !== "" && r.phone.replace(/\D/g, "").includes(digits);
      if (!nameHit && !phoneHit) return false;
    }
    return true;
  });
  const pageRows = filtered.slice(0, visible);
  // Footer button state: reveal loaded rows `PAGE` at a time first; once they
  // all show and the server holds more, the button loads the next server page.
  const moreLoaded = filtered.length > visible;
  const moreOnServer = !moreLoaded && nextCursor !== null;

  const selectFilter = (next: CaseStatus | null): void => {
    setFilter(next);
    setVisible(PAGE); // paging restarts per filter change
    // Keep `?status=` in step so the filtered view is shareable and the back
    // button behaves; `replace` avoids a history entry per chip click.
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === null) params.delete("status");
        else params.set("status", next);
        return params;
      },
      { replace: true },
    );
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-foreground font-serif text-[30px] font-semibold leading-[42px]">
            Referrals
          </h2>
          {/* An org_admin's list is org-wide (the server scopes by role), so
              the subtitle says so per the design (Figma dR8cJ6hRSW5eERToo64boe
              node 2340:385); a regular user sees only their own referrals. */}
          <p className="text-foreground mt-1 text-[17px] leading-6">
            {isOrgAdmin ? "Manager view" : "Patients you've referred and where each one stands."}
          </p>
        </div>
        {canEnroll && (
          <Button variant="primary" size="lg" onClick={() => setReferOpen(true)} className="gap-2">
            <img src={iconPerson} alt="" aria-hidden="true" className="size-[18px]" />
            Refer a patient
          </Button>
        )}
      </div>

      <ReferSomeoneModal open={referOpen} onClose={() => setReferOpen(false)} orgId={orgId} />

      {loadError && (
        <Alert tone="error" title={loadError} className="mt-6">
          Reload the page to try again. If it keeps happening, use &ldquo;Need help? Call&rdquo; at
          the top of the page.
        </Alert>
      )}

      {rows === null && !loadError && (
        <div className="text-slate-teal mt-8 flex items-center gap-3">
          <Spinner /> <span>Loading referrals…</span>
        </div>
      )}

      {rows !== null && rows.length === 0 && (
        <div className="border-border-strong mt-8 rounded-2xl border bg-white px-6 py-10 text-center">
          <p className="text-foreground font-semibold">No referrals yet.</p>
          <p className="text-slate-teal mt-1">
            {canEnroll
              ? "Patients you refer will show up here with where each one stands. To refer your first patient, use the “Refer a patient” button above."
              : "Patients you refer will show up here with where each one stands. Referrals open up once your organization is activated."}
          </p>
        </div>
      )}

      {rows !== null && rows.length > 0 && (
        <>
          <div className="mt-5 flex flex-wrap items-center gap-2.5">
            <button
              type="button"
              onClick={() => selectFilter(null)}
              aria-pressed={filter === null}
              className={cn(
                "min-h-[42px] cursor-pointer rounded-lg px-[18px] py-2 text-base font-semibold leading-[22px]",
                filter === null
                  ? "bg-deep-navy text-white"
                  : "border-haze text-foreground hover:bg-ice border bg-white",
              )}
            >
              All {rows.length}
            </button>
            {CASE_STATUSES.map((cs) => {
              const n = counts.get(cs) ?? 0;
              if (n === 0) return null;
              return (
                <button
                  key={cs}
                  type="button"
                  onClick={() => selectFilter(filter === cs ? null : cs)}
                  aria-pressed={filter === cs}
                  className={cn(
                    "flex min-h-[42px] cursor-pointer items-center gap-2 rounded-lg px-4 py-2 text-base font-semibold leading-[22px]",
                    CASE_STATUS_CHIP_CLASS[cs],
                    filter === cs && "ring-2 ring-current",
                  )}
                >
                  {cs}
                  <span className="rounded-full bg-white/70 px-2 py-0.5 text-[13px] font-bold leading-[18px] text-current">
                    {n}
                  </span>
                </button>
              );
            })}
          </div>

          <label className="text-slate-teal mt-3 flex items-center gap-2.5 text-[15px] font-semibold">
            Find
            <input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setVisible(PAGE);
              }}
              placeholder="Name or phone number"
              className="border-haze text-foreground min-h-[46px] w-full max-w-3xl flex-1 rounded-lg border bg-white px-4 text-base font-normal"
            />
          </label>

          <div className="border-border-strong mt-4 overflow-x-auto rounded-2xl border bg-white">
            <table className="w-full text-left">
              <thead className="bg-deep-navy">
                <tr className="text-[11px] font-semibold uppercase leading-[18px] tracking-[1.1px] text-white">
                  <th className="py-3 pl-6 pr-4 font-semibold">Patient</th>
                  <th className="px-4 py-3 font-semibold">Referred</th>
                  <th className="px-4 py-3 font-semibold">By</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Current status</th>
                  <th className="px-4 py-3 font-semibold">Last change</th>
                  <th className="py-3 pl-4 pr-6">
                    <span className="sr-only">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={
                      row.resultsShared
                        ? () => void navigate(`/orgs/${orgId}/forms/${row.id}`)
                        : undefined
                    }
                    className={cn(
                      "border-haze border-t",
                      row.resultsShared && "hover:bg-ice/60 cursor-pointer",
                    )}
                  >
                    <td className="text-foreground whitespace-nowrap py-4 pl-6 pr-4 text-[17px] font-semibold leading-6">
                      <span className="inline-flex items-center gap-1.5">
                        {row.fullName || <span className="text-slate-teal">—</span>}
                        {row.referrerProfileIncomplete && <IncompleteProfileMarker />}
                      </span>
                    </td>
                    <td className="text-foreground whitespace-nowrap px-4 py-4 text-[17px] font-medium leading-6">
                      {fmtSent(row.createdAt)}
                    </td>
                    <td className="text-foreground whitespace-nowrap px-4 py-4 text-base leading-[22px]">
                      {byLabel(row)}
                    </td>
                    <td className="px-4 py-4">
                      {/* A redacted row has no case status. */}
                      {row.resultsShared && row.caseStatus !== null ? (
                        <CaseStatusLabel status={row.caseStatus} />
                      ) : (
                        <NotShared />
                      )}
                    </td>
                    <td className="text-foreground min-w-64 px-4 py-4 text-[17px] leading-6">
                      {row.resultsShared ? (row.caseStatusDetail ?? <NotShared />) : <NotShared />}
                    </td>
                    <td className="text-slate-teal whitespace-nowrap px-4 py-4 text-base leading-[22px]">
                      {/* A redacted row withholds the change stamp with the rest. */}
                      {row.resultsShared && row.lastChangeAt !== null ? (
                        fmtLastChange(row.lastChangeAt)
                      ) : (
                        <NotShared />
                      )}
                    </td>
                    <td className="py-4 pl-4 pr-6">
                      {/* The detail route 404s an unshared broker row, so it gets no link. */}
                      {row.resultsShared && (
                        <Link
                          to={`/orgs/${orgId}/forms/${row.id}`}
                          className="text-ember hover:text-ember-dark font-semibold underline underline-offset-2"
                        >
                          Open
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="border-haze flex flex-wrap items-center justify-between gap-3 border-t px-6 py-4">
              <p className="text-slate-teal text-base leading-[22px]">
                Showing {pageRows.length} of {filtered.length}
                {nextCursor !== null && " loaded"}
              </p>
              {moreLoaded && (
                <Button variant="soft" size="sm" onClick={() => setVisible((v) => v + PAGE)}>
                  Show {Math.min(PAGE, filtered.length - visible)} more
                </Button>
              )}
              {moreOnServer && (
                <Button
                  variant="soft"
                  size="sm"
                  onClick={() => void onLoadMore()}
                  disabled={loadingMore}
                >
                  {loadingMore ? "Loading…" : "Load more"}
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** Cell for a column the patient chose not to share with their broker. */
function NotShared(): ReactNode {
  return <span className="text-slate-teal">Not shared</span>;
}
